import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Lock, Trash2, Unlock, UserPlus, ShieldCheck, Shield } from "lucide-react";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setUsers(await api.adminListUsers());
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Gate: only admins may view
  if (user && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const toggleBlock = async (u) => {
    try {
      if (u.blocked) {
        await api.adminUnblockUser(u.id);
        toast.success(`${u.email} unblocked`);
      } else {
        await api.adminBlockUser(u.id);
        toast.success(`${u.email} blocked`);
      }
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Action failed");
    }
  };

  const deleteUser = async (u) => {
    try {
      await api.adminDeleteUser(u.id);
      toast.success(`${u.email} deleted`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline text-klystic-sky mb-2 flex items-center gap-2">
            <ShieldCheck size={12} /> Admin
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-klystic-ink">
            User management
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Add, block, and remove testers. Admins are set via the <code className="font-mono text-xs">ADMIN_EMAILS</code> env allowlist.
          </p>
        </div>
        <AddUserDialog
          open={addOpen}
          setOpen={setAddOpen}
          onCreated={load}
        />
      </div>

      <div className="bg-white border border-slate-200">
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No users yet.</div>
        ) : (
          <Table data-testid="admin-users-table">
            <TableHeader>
              <TableRow className="border-b border-slate-200">
                <TableHead className="overline text-slate-500">User</TableHead>
                <TableHead className="overline text-slate-500">Role</TableHead>
                <TableHead className="overline text-slate-500">2FA</TableHead>
                <TableHead className="overline text-slate-500">Status</TableHead>
                <TableHead className="overline text-slate-500">Created</TableHead>
                <TableHead className="overline text-slate-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const self = user && user.id === u.id;
                return (
                  <TableRow
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    className="border-b border-slate-100"
                  >
                    <TableCell>
                      <div className="font-medium text-slate-900">
                        {u.name || u.email.split("@")[0]}
                      </div>
                      <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                    </TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <Badge className="rounded-none bg-klystic-ink text-white font-mono text-[10px] tracking-widest">
                          <Shield size={10} className="mr-1" /> ADMIN
                        </Badge>
                      ) : (
                        <Badge className="rounded-none bg-slate-100 text-slate-700 font-mono text-[10px] tracking-widest">
                          USER
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.totp_confirmed ? (
                        <span className="text-xs text-emerald-700 font-mono">enrolled</span>
                      ) : (
                        <span className="text-xs text-amber-700 font-mono">pending</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.blocked ? (
                        <Badge className="rounded-none bg-red-100 text-red-800 border border-red-200 font-mono text-[10px] tracking-widest">
                          BLOCKED
                        </Badge>
                      ) : (
                        <Badge className="rounded-none bg-emerald-100 text-emerald-800 border border-emerald-200 font-mono text-[10px] tracking-widest">
                          ACTIVE
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`admin-toggle-block-${u.id}`}
                          disabled={self}
                          onClick={() => toggleBlock(u)}
                          className="rounded-none h-8 border-slate-300"
                          title={self ? "Cannot block yourself" : u.blocked ? "Unblock" : "Block"}
                        >
                          {u.blocked ? <Unlock size={14} /> : <Lock size={14} />}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`admin-delete-${u.id}`}
                              disabled={self}
                              className="rounded-none h-8 border-slate-300 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                              title={self ? "Cannot delete yourself" : "Delete"}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-none border border-slate-200">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.email}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This permanently removes the account. Their sessions and results remain in the tracker.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-none border-slate-300">
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                data-testid={`admin-confirm-delete-${u.id}`}
                                onClick={() => deleteUser(u)}
                                className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function AddUserDialog({ open, setOpen, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.adminAddUser({ email, password, name });
      toast.success(
        `Invite created for ${email}. They must sign in and enroll 2FA to activate.`
      );
      setEmail("");
      setName("");
      setPassword("");
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed to add user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="admin-add-user-btn"
          className="bg-klystic-ink hover:bg-klystic-ink-dark text-white rounded-none h-10 px-4"
        >
          <UserPlus size={14} className="mr-2" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border border-slate-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Invite a tester</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} data-testid="admin-add-user-form" className="space-y-4">
          <div>
            <Label className="overline text-slate-500">Email</Label>
            <Input
              data-testid="admin-add-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-none border-slate-300"
            />
          </div>
          <div>
            <Label className="overline text-slate-500">Display name</Label>
            <Input
              data-testid="admin-add-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-none border-slate-300"
            />
          </div>
          <div>
            <Label className="overline text-slate-500">Temporary password (8+ chars)</Label>
            <Input
              data-testid="admin-add-password"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              className="rounded-none border-slate-300 font-mono"
            />
            <p className="mt-2 text-xs text-slate-500">
              Share this with the invited user. On first sign-in they'll enroll 2FA on their own device.
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="rounded-none border-slate-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid="admin-add-submit"
              className="rounded-none bg-klystic-ink hover:bg-klystic-ink-dark text-white"
            >
              {busy ? "Creating…" : "Create invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
